import { aiModels, imageModels, type AiModelId, type AiModelOption, type ImageModelId } from "./models";

type ModelChoices<T extends string> = readonly [T] | readonly [T, T] | readonly [T, T, T];
const chatModels = ["gpt-5.6-luna", "gpt-5.6-terra", "gpt-5.6-sol"] as const;
const nutritionModels = ["gemini-3.8-flash", "gpt-6.1-sol", "claude-opus-5-5"] as const;

/** This is the only place defining each task's choices and default. Order: fast, standard, strong. */
export const aiModelTasks = {
  nutrition: { models: nutritionModels, default: "gpt-6.1-sol" },
  nutritionAutomation: { models: nutritionModels, default: "gpt-6.1-sol" },
  training: { models: chatModels, default: "gpt-5.6-luna" },
  sales: { models: chatModels, default: "gpt-5.6-luna" },
  advisor: { models: chatModels, default: "gpt-5.6-luna" },
  traineeReport: { models: chatModels, default: "gpt-5.6-luna" },
} as const satisfies Record<string, { models: ModelChoices<AiModelId>; default: AiModelId }>;

export type AiTask = keyof typeof aiModelTasks;
export type AiTaskModelId<T extends AiTask> = (typeof aiModelTasks)[T]["models"][number];

// Explicit compatibility for saved forms, automations and older mobile clients.
// Arbitrary provider/model strings are never accepted as a new request.
const legacyModelAliases = {
  // still offered for chat tasks; saved nutrition forms and automations move to 6.1 Sol
  "gpt-5.6-sol": "gpt-6.1-sol",
  "gpt-5.6-terra": "gpt-6.1-sol",
  "gpt-5.6-luna-medium": "gpt-5.6-luna",
  "gpt-5.4-mini-xhigh": "gpt-5.6-luna",
  "gpt-5.4-mini": "gpt-5.6-luna",
  "gpt-5.4-medium": "gpt-5.6-terra",
  "gpt-5.4-xhigh": "gpt-5.6-terra",
  "gpt-5.4": "gpt-5.6-terra",
  "gpt-5.5-medium": "gpt-5.6-sol",
  "gpt-5.5": "gpt-5.6-sol",
  "gpt-5.2": "gpt-5.6-terra",
  "gpt-o4-mini": "gpt-5.6-luna",
  "gpt-o3-mini": "gpt-5.6-luna",
  "claude-opus-4-8": "gpt-5.6-sol",
  "gemini-2.0-flash-001": "gemini-3.8-flash",
  "gemini-2.5-flash": "gemini-3.8-flash",
  "gemini-3-flash-preview": "gemini-3.8-flash",
  "gemini-3.0-flash": "gemini-3.8-flash",
  "gemini-3.5-flash": "gemini-3.8-flash",
  "gemini-2.0-pro-exp-02-05": "gpt-5.6-sol",
  "gemini-2.5-pro": "gpt-5.6-sol",
  "gemini-3.0-pro": "gpt-5.6-sol",
  "gemini-3-pro-preview": "gpt-5.6-sol",
  "gemini-3.7-flash": "gemini-3.8-flash",
} as const satisfies Record<string, AiModelId>;

export type AiModelInput = AiModelId | keyof typeof legacyModelAliases;

export const getAiModelIds = <T extends AiTask>(task: T): (typeof aiModelTasks)[T]["models"] =>
  aiModelTasks[task].models;
export const getDefaultAiModelId = <T extends AiTask>(task: T): AiTaskModelId<T> => aiModelTasks[task].default;
export const isAiModelIdForTask = <T extends AiTask>(task: T, value?: string | null): value is AiTaskModelId<T> =>
  (aiModelTasks[task].models as readonly string[]).includes(value ?? "");

export function resolveAiModelId<T extends AiTask>(task: T, value?: string | null): AiTaskModelId<T> {
  if (isAiModelIdForTask(task, value)) return value;
  const alias =
    value && Object.hasOwn(legacyModelAliases, value)
      ? legacyModelAliases[value as keyof typeof legacyModelAliases]
      : undefined;
  return isAiModelIdForTask(task, alias) ? alias : getDefaultAiModelId(task);
}

export const isAllowedAiModelInput = (task: AiTask, value: string) =>
  isAiModelIdForTask(task, value) || Object.hasOwn(legacyModelAliases, value);

export function getAiModelOption<T extends AiTask>(
  task: T,
  value?: string | null,
): AiModelOption & { id: AiTaskModelId<T> } {
  const id = resolveAiModelId(task, value);
  return { id, ...aiModels[id] };
}

export const getAiModelOptions = <T extends AiTask>(task: T) =>
  aiModelTasks[task].models.map((id) => getAiModelOption(task, id));

export const getAiModelSelectOptions = (task: AiTask) =>
  getAiModelOptions(task).map((option) => ({ value: option.id, label: option.label, shortLabel: option.label }));

export const imageModelTasks = {
  dishImage: {
    models: ["google-nano-banana-lite", "google-nano-banana", "openai"],
    default: "google-nano-banana-lite",
  },
  coverImage: {
    models: ["google-nano-banana-lite", "google-nano-banana", "openai"],
    default: "google-nano-banana-lite",
  },
} as const satisfies Record<string, { models: ModelChoices<ImageModelId>; default: ImageModelId }>;

export const getImageModelSelectOptions = (task: keyof typeof imageModelTasks) =>
  imageModelTasks[task].models.map((id) => ({ value: id, label: imageModels[id].label }));
